var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Resource } from './Resource.js';
import { Bkper } from './Bkper.js';
import * as UserService from '../service/user-service.js';
/**
 * This class defines the Billing information for a [[User]].
 *
 * The Billing information includes the plan, the admin email, and the billing portal URL.
 *
 * @public
 */
export class Billing extends Resource {
    constructor(json, config) {
        super(json);
        this.config = config;
    }
    /** @internal */
    getConfig() {
        return this.config || Bkper.globalConfig;
    }
    /**
     * Tells if billing is enabled for the User.
     *
     * @returns True if billing is enabled for the User
     */
    isEnabled() {
        return this.payload.enabled;
    }
    /**
     * Gets the email for the User.
     *
     * @returns The User's email
     */
    getEmail() {
        return this.payload.email;
    }
    /**
     * Gets the hosted domain for the User.
     *
     * @returns The User's hosted domain
     */
    getHostedDomain() {
        return this.payload.hostedDomain;
    }
    /**
     * Gets the current plan of the User.
     *
     * @returns The User's plan
     */
    getPlan() {
        return this.payload.plan;
    }
    /**
     * Tells if the User's current plan payment is overdue.
     *
     * @returns True if the plan payment is overdue
     */
    isPlanOverdue() {
        return this.payload.planOverdue;
    }
    /**
     * Gets the admin email for this User's billing account.
     *
     * @returns The billing admin email
     */
    getAdminEmail() {
        return this.payload.adminEmail;
    }
    /**
     * Tells if the User has started the trial period.
     *
     * @returns True if the User has started the trial period
     */
    hasStartedTrial() {
        return this.payload.startedTrial;
    }
    /**
     * Gets the number of days left in User's trial period.
     *
     * @returns The number of days left in trial period
     */
    getDaysLeftInTrial() {
        return this.payload.daysLeftInTrial;
    }
    /**
     * Gets the number of total transactions this month for the User's billing account.
     *
     * @returns The number of total transactions this month
     */
    getTotalTransactionsThisMonth() {
        const total = this.payload.totalTransactionsThisMonth;
        // int64 totals arrive as JSON strings
        return total != null ? +total : undefined;
    }
    /**
     * Gets the number of total transactions this year for the User's billing account.
     *
     * @returns The number of total transactions this year
     */
    getTotalTransactionsThisYear() {
        const total = this.payload.totalTransactionsThisYear;
        // int64 totals arrive as JSON strings
        return total != null ? +total : undefined;
    }
    /**
     * Gets the authoritative rolling monthly average of posted transactions.
     *
     * @returns The transaction average, or undefined when unavailable for the current plan
     */
    getTransactionsAverage() {
        return this.payload.transactionsAverage;
    }
    /**
     * Gets the confirmed average monthly transaction capacity.
     *
     * @returns The transaction capacity, or undefined when unavailable for the current plan
     */
    getTransactionsCapacity() {
        return this.payload.transactionsCapacity;
    }
    /**
     * Gets the transaction counts associated to the User's billing account.
     *
     * @returns The transaction counts associated to the User's billing account
     */
    getCounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const countsPayload = yield UserService.getBillingCounts(this.getConfig());
            return countsPayload;
        });
    }
    /**
     * Gets the URL to redirect the User to the billing portal.
     *
     * @param returnUrl - The URL to return to after the User has been redirected to the billing portal
     *
     * @returns The URL to redirect the User to the billing portal
     */
    getPortalUrl(returnUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            const urlPayload = yield UserService.getBillingPortalUrl(returnUrl, this.getConfig());
            return urlPayload === null || urlPayload === void 0 ? void 0 : urlPayload.url;
        });
    }
    /**
     * Gets the URL to redirect the User to the billing checkout.
     *
     * @param plan - The plan to checkout
     * @param successUrl - The URL to redirect to after the User has successfully checked out
     * @param cancelUrl - The URL to redirect to in case the checkout is cancelled
     * @param cycle - The billing cycle to checkout - "monthly" (default) or "yearly"
     *
     * @returns The URL to redirect the User to the billing checkout
     */
    getCheckoutUrl(plan, successUrl, cancelUrl, cycle) {
        return __awaiter(this, void 0, void 0, function* () {
            const urlPayload = yield UserService.getBillingCheckoutUrl(plan, successUrl, cancelUrl, cycle, this.getConfig());
            return urlPayload === null || urlPayload === void 0 ? void 0 : urlPayload.url;
        });
    }
}
//# sourceMappingURL=Billing.js.map